import React, { useMemo, useEffect, useState } from "react";
import { Accordion } from "react-bootstrap";
import { Link, useNavigate } from 'react-router-dom';
import PageTitle from "../../layouts/PageTitle";
import Loader from '../Loader/Loader';
import { useTable, useGlobalFilter, usePagination } from "react-table";
import { COLUMNS } from "./columns";
import "../../components/Filters/filtering.css";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { 
  getDepositorsAction, 
  bulkRefreshStatusAction,
  closeDepositorAction,
  refreshDepositorStatusAction 
} from "../../../store/actions/DepositorsActions";
import { getCompaniesForSelectAction } from "../../../store/actions/CompaniesActions";
import DepositsFilterForm from "../../components/Filters/DepositsFilterForm";
import StatisticsCards from "../../components/StatisticsCards";
import swal from "sweetalert";
import DepositorsService from "../../../services/DepositorsService";

const ManageDepositors = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const columns = useMemo(() => COLUMNS, []);
  const [activeKey, setActiveKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [statisticsData, setStatisticsData] = useState({
    active_count: 0,
    inactive_count: 0,
    pending_count: 0,
    total_count: 0
  });
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  
  const toggleAccordion = () => {
    setActiveKey(prevKey => (prevKey === '1' ? null : '1'));
  };

  const { 
    listdata: data, 
    pagination, 
    loading, 
    depositorsCount,
    error,
    success 
  } = useSelector((state) => state.depositors);

  console.log('listdata', data)

  const { selectOptions: companies } = useSelector((state) => state.companies);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canNextPage,
    canPreviousPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      manualGlobalFilter: true,
      pageCount: pagination?.totalPages || 0,
      initialState: { 
        pageIndex: currentPage - 1, 
        pageSize: currentPageSize 
      },
    },
    useGlobalFilter,
    usePagination
  );

  useEffect(() => {
    fetchStatistics();
  }, []);

  const handleEdit = (type, typeid) => {
    navigate(`/depositors/${type}/${typeid}`)
  }

  const handleClose = (id, name) => {
    swal({
      title: "Are you sure?",
      text: `This will close depositor: ${name}`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    })
    .then((willClose) => {
      if (willClose) {
        dispatch(closeDepositorAction(id));
      }
    });
  }

  const handleRefreshStatus = (id, name) => {
    swal({
      title: "Refresh Status",
      text: `Refresh KYC status for depositor: ${name}?`,
      icon: "info",
      buttons: true,
    })
    .then((willRefresh) => {
      if (willRefresh) {
        dispatch(refreshDepositorStatusAction(id));
      }
    });
  }

  const handleBulkRefresh = () => {
    swal({
      title: "Bulk Refresh All Statuses",
      text: "This will refresh KYC status for all depositors. Continue?",
      icon: "warning",
      buttons: true,
    })
    .then((willRefresh) => {
      if (willRefresh) {
        dispatch(bulkRefreshStatusAction());
      }
    });
  }

  const fetchStatistics = async () => {
    setStatisticsLoading(true);
    try {
      const response = await DepositorsService.getDepositorsStatistics();
      if (response.success) {
        setStatisticsData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch depositors statistics:', error);
    } finally {
      setStatisticsLoading(false);
    }
  };

  const statsConfig = [
    {
      title: "Active Depositors",
      value: statisticsData?.active_count || 0,
      format: "number",
      icon: "las la-user-check",
      color: "success",
    },
    {
      title: "Inactive Depositors",
      value: statisticsData?.inactive_count,
      format: "number",
      icon: "las la-user-times",
      color: "danger",
    },
    {
      title: "Pending Depositors",
      value: statisticsData?.pending_count,
      format: "number",
      icon: "las la-user-clock",
      color: "warning",
    },
    {
      title: "Total Depositors",
      value: statisticsData?.total_count,
      format: "number",
      icon: "las la-users",
      color: "primary",
    },
  ];

  const depositorFilterFields = [
    { 
      name: "search", 
      label: "Search", 
      type: "text", 
      placeholder: "Search by name, phone, email, city, district, or state" 
    },
    {
      name: "companyId",
      label: "Company",
      type: "companySearchSelect",
      required: false,
      allowClear: true,
    },
    {
      name: "branchId",
      label: "Branch",
      type: "branchSearchSelect",
      required: false,
      allowClear: true,
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: "Active", label: "Active" },
        { value: "Inactive", label: "Inactive" },
        { value: "Pending", label: "Pending" },
        { value: "Rejected", label: "Rejected" },
      ],
    },
  ];

  const svg1 = (
    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="5" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="19" cy="12" r="2"></circle>
      </g>
    </svg>
  );

  const filterData = (filters) => {
    const cleanedObj = Object.fromEntries(
      Object.entries(filters).filter(
        ([_, value]) =>
          value !== null &&
          value !== '' &&
          value !== undefined &&
          (typeof value !== "object" || Object.keys(value).length > 0)
      )
    );
    dispatch(getDepositorsAction({ page: 1, limit: currentPageSize, ...cleanedObj }));
  }

  // Load depositors on component mount and when pagination changes
  useEffect(() => {
    const params = {
      page: currentPage,
      limit: currentPageSize,
    };
    dispatch(getDepositorsAction(params));
  }, [dispatch, currentPage, currentPageSize]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber + 1);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setCurrentPageSize(Number(newPageSize));
    setCurrentPage(1); // Reset to first page when page size changes
  };
  
  const { pageIndex, pageSize, globalFilter } = state;

  useEffect(() => {
    dispatch(getCompaniesForSelectAction());
  }, [dispatch]);

  return (
    <div className="custom-listing">
      {loading && <div><Loader /></div>}
      <PageTitle activeMenu="Depositors" motherMenu="Finance" />

      {/* Filter and Actions */}
      <div className="card">
        <div className="card-body px-4 py-3 pb-0">
          <div className="form-head d-flex flex-wrap justify-content-between align-items-center m-0">
            <h2 className="text-black font-w600 mb-0 me-auto pr-3">Depositors List</h2>
            <Dropdown className="custom-dropdown me-3">
              <Dropdown.Menu className="dropdown-menu-right">
                <Dropdown.Item onClick={handleBulkRefresh}>Bulk Refresh All Statuses</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Link to="/depositors/create" className="btn btn-primary btn-rounded me-3">
              <i className="las la-plus scale5 me-3"></i>Add Depositor
            </Link>
            <Link to="#" className="btn btn-primary btn-rounded me-3">
              <i className="las la-download scale5 me-3"></i>Download Report
            </Link>
            <button className="btn btn-primary" onClick={toggleAccordion}>
              <i className="flaticon-381-funnel"></i>
            </button>
          </div>
        </div>
        <Accordion activeKey={activeKey} className="accordion">
          <Accordion.Item className="accordion-item" eventKey="1">
            <Accordion.Collapse eventKey="1">
              <div className="accordion-body">
                <div className="basic-form">
                  <DepositsFilterForm  
                    fields={depositorFilterFields}
                    onSubmit={(filters) => filterData(filters)}
                  />
                </div>
              </div>
            </Accordion.Collapse>
          </Accordion.Item>
        </Accordion>
      </div>

      <StatisticsCards stats={statsConfig} loading={statisticsLoading} />

      {/* Data Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table {...getTableProps()} className="table table-responsive-md">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()} key={column.id}>
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center">
                      No data available
                    </td>
                  </tr>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => {
                          if (cell.column.id === 'actions') {
                            return (
                              <td {...cell.getCellProps()} key={cell.column.id}>
                                <Dropdown>
                                  <Dropdown.Toggle
                                    variant="success"
                                    className="light sharp i-false btn-xs"
                                  >
                                    {svg1}
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => handleEdit('edit', cell.row.original.id)}>
                                      Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleEdit('view', cell.row.original.id)}>
                                      View Details
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleEdit('kyc', cell.row.original.id)}>
                                      Manage KYC
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleRefreshStatus(cell.row.original.id, cell.row.original.name)}>
                                      Refresh Status
                                    </Dropdown.Item>
                                    {cell.row.original.status !== 'Inactive' && (
                                      <Dropdown.Item 
                                        className="text-danger"
                                        onClick={() => handleClose(cell.row.original.id, cell.row.original.name)}
                                      >
                                        Close Depositor
                                      </Dropdown.Item>
                                    )}
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>
                            );
                          }
                          return (
                            <td {...cell.getCellProps()} key={cell.column.id}>
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="dataTables_info">
              Showing{" "}
              <strong>
                {pagination?.totalRecords > 0
                  ? (currentPage - 1) * currentPageSize + 1
                  : 0}
              </strong>{" "}
              to{" "}
              <strong>
                {Math.min(
                  currentPage * currentPageSize,
                  pagination?.totalRecords || 0
                )}
              </strong>{" "}
              of{" "}
              <strong>{pagination?.totalRecords || 0}</strong>{" "}
              entries
            </div>
            
            <div className="dataTables_paginate paging_simple_numbers d-flex align-items-center">
              <div className="me-3">
                <select
                  className="form-select form-select-sm form-control"
                  value={currentPageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  style={{ width: "auto" }}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="pagination-controls d-flex">
                <button
                  className="paginate_button previous btn btn-sm btn-primary me-2"
                  onClick={() => handlePageChange(0)}
                  disabled={!canPreviousPage}
                >
                  <i className="fa fa-angle-double-left"></i>
                </button>
                
                <button
                  className="paginate_button previous btn btn-sm btn-primary me-2"
                  onClick={() => handlePageChange(currentPage - 2)}
                  disabled={!canPreviousPage}
                >
                  <i className="fa fa-angle-left"></i>
                </button>
                
                <span className="mx-3 d-flex align-items-center">
                  Page{" "}
                  <strong className="mx-2">
                    {currentPage} of {pagination?.totalPages || 0}
                  </strong>
                </span>
                
                <button
                  className="paginate_button next btn btn-sm btn-primary ms-2"
                  onClick={() => handlePageChange(currentPage)}
                  disabled={!canNextPage}
                >
                  <i className="fa fa-angle-right"></i>
                </button>
                
                <button
                  className="paginate_button next btn btn-sm btn-primary ms-2"
                  onClick={() => handlePageChange((pagination?.totalPages || 1) - 1)}
                  disabled={!canNextPage}
                >
                  <i className="fa fa-angle-double-right"></i>
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageDepositors;